"use client";

import * as React from "react";
import {
  ThumbsUp,
  Heart,
  Frown,
  Smile,
  Angry,
  type LucideIcon,
} from "lucide-react";
import { PopoverContent } from "@/components/ui/popover";
import {
  articlePopoverCompactSheetClass,
  articlePopoverFitContentClass,
  useHeaderPopoverLayout,
  type ArticlePopoverPlacement,
} from "./popover-placement";
import { CORE_COLORS, CORE_FONT } from "@/lib/theme/core";

type ReactionKey = "thumbs" | "heart" | "cry" | "laugh" | "angry";

type Reaction = {
  key: ReactionKey;
  label: string;
  Icon: LucideIcon;
};

const REACTIONS: Reaction[] = [
  { key: "thumbs", label: "讚好", Icon: ThumbsUp },
  { key: "heart", label: "喜愛", Icon: Heart },
  { key: "cry", label: "難過", Icon: Frown },
  { key: "laugh", label: "好笑", Icon: Smile },
  { key: "angry", label: "憤怒", Icon: Angry },
];

const INITIAL_COUNTS: Record<ReactionKey, number> = {
  thumbs: 15,
  heart: 9,
  cry: 3,
  laugh: 5,
  angry: 1,
};

export { REACTIONS, type ReactionKey };

/** Total across all reactions — what the collapsed trigger shows. */
export function reactionTotal(counts: Record<ReactionKey, number>) {
  return Object.values(counts).reduce((a, b) => a + b, 0);
}

export const INITIAL_REACTION_COUNTS = INITIAL_COUNTS;

export function ReactionPopoverContent({
  placement = "rail",
  selected: selectedProp,
  counts: countsProp,
  onChange,
}: {
  placement?: ArticlePopoverPlacement;
  /**
   * Optional controlled state. The popover keeps its own by default so the four
   * consumers that only render the strip are unaffected; `article-header.tsx`
   * lifts it so its trigger can show which reaction was picked — QA: the
   * trigger stayed a generic 😀+ no matter what you chose.
   */
  selected?: readonly ReactionKey[];
  counts?: Record<ReactionKey, number>;
  onChange?: (next: {
    selected: ReactionKey[];
    counts: Record<ReactionKey, number>;
  }) => void;
} = {}) {
  const layout = useHeaderPopoverLayout(placement, {
    headerAlign: "center",
    compactFitContent: true,
  });

  const [ownCounts, setOwnCounts] = React.useState(INITIAL_COUNTS);
  const [ownSelected, setOwnSelected] = React.useState<ReactionKey[]>([]);

  const controlled = countsProp !== undefined && selectedProp !== undefined;
  const counts = controlled ? countsProp : ownCounts;
  const selected = controlled ? selectedProp : ownSelected;

  /**
   * Reactions are INDEPENDENT toggles.
   *
   * Picking one used to clear whichever was already chosen — the model held a
   * single key — so a reader could never say a piece was both 讚好 and 好笑.
   * QA: "allow giving multiple reactions".
   */
  const handleClick = (key: ReactionKey) => {
    const nextCounts = { ...counts };
    const on = selected.includes(key);
    nextCounts[key] = on
      ? Math.max(0, counts[key] - 1)
      : counts[key] + 1;
    const nextSelected = on
      ? selected.filter((k) => k !== key)
      : [...selected, key];

    if (!controlled) {
      setOwnCounts(nextCounts);
      setOwnSelected(nextSelected);
    }
    onChange?.({ selected: nextSelected, counts: nextCounts });
  };

  return (
    <PopoverContent
      side={layout.side}
      align={layout.align}
      sideOffset={12}
      collisionPadding={layout.collisionPadding}
      className={
        layout.fitContent
          ? `${articlePopoverFitContentClass()}${layout.isCompactHeader ? ` ${articlePopoverCompactSheetClass()}` : ""}`
          : `bg-white p-0${layout.isCompactHeader ? ` ${articlePopoverCompactSheetClass()}` : ""}`
      }
      style={{
        zIndex: 60,
        ...(layout.width != null ? { width: layout.width } : {}),
        borderRadius: "8px",
        border: `1px solid rgba(0,0,0,0.15)`,
        fontFamily: CORE_FONT.sans,
      }}
    >
      {/* SCMP pattern: circular icon buttons in a strip */}
      <div
        className={`flex items-start py-[14px] ${
          layout.isCompactHeader
            ? "gap-[8px] px-[12px]"
            : "gap-[12px] px-[16px]"
        }`}
      >
        {REACTIONS.map(({ key, label, Icon }) => {
          const active = selected.includes(key);
          return (
            <button
              key={key}
              type="button"
              aria-label={label}
              aria-pressed={active}
              onClick={() => handleClick(key)}
              className="group flex flex-col items-center gap-[6px] min-h-[60px] focus-visible:outline-none"
            >
              {/* Selected state INVERTS: white circle + green ring, with the
                  glyph OUTLINED in green (see below). Every filled variant we
                  tried lost the face — a solid shape has no eyes or mouth.
                  The resting colour is carried as `--bg` (a custom property)
                  rather than an inline `background-color`. An inline
                  declaration outranks every class, so the
                  `group-hover:bg-*` here had never applied — hovering a
                  reaction did nothing. Both states are classes now, so the
                  hover variant wins normally. */}
              <span
                className="inline-flex items-center justify-center size-[44px] rounded-full bg-[color:var(--bg)] transition-[background-color,transform] duration-150 ease-out motion-reduce:transition-none group-hover:bg-[color:var(--green)] group-active:scale-[0.94] group-focus-visible:ring-2 group-focus-visible:ring-offset-2 group-focus-visible:ring-[color:var(--green)]"
                style={
                  {
                    ["--bg" as string]: active
                      ? "#ffffff"
                      : CORE_COLORS.brandGreenDeep,
                    boxShadow: active
                      ? `inset 0 0 0 2px ${CORE_COLORS.brandGreen}`
                      : undefined,
                    ["--green" as string]: active
                      ? "#ffffff"
                      : CORE_COLORS.brandGreen,
                  } as React.CSSProperties
                }
              >
                {/* The selected glyph is OUTLINED, never filled. Filling it
                    with the brand green made the whole face a solid green
                    shape — the eyes and mouth are strokes in the same colour,
                    so they vanished into it. QA: "after clicking on the emoji
                    with faces, the enter face become green, cannot see the
                    facial expression". */}
                <Icon
                  className="size-[18px]"
                  color={active ? CORE_COLORS.brandGreen : "white"}
                  fill="none"
                  strokeWidth={active ? 2.4 : 1.8}
                />
              </span>
              <span
                className="tabular-nums transition-colors duration-150 motion-reduce:transition-none"
                style={{
                  fontFamily: CORE_FONT.sans,
                  fontSize: "11px",
                  fontWeight: active ? 700 : 500,
                  color: active
                    ? CORE_COLORS.brandGreen
                    : CORE_COLORS.textPrimary,
                  lineHeight: "14px",
                }}
              >
                {counts[key]}
              </span>
            </button>
          );
        })}
      </div>

      {/* sr-only summary for screen readers */}
      <span role="status" aria-live="polite" className="sr-only">
        {selected.length
          ? `已選擇 ${selected
              .map((k) => REACTIONS.find((r) => r.key === k)?.label)
              .filter(Boolean)
              .join("、")}`
          : ""}
      </span>
    </PopoverContent>
  );
}
